import React, { useContext, useEffect, useState } from 'react';
import { 
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Tab,
    Tabs,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import Divider from '@mui/material/Divider';


import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import LinearProgress from '@mui/material/LinearProgress';



import { useAuth0 } from "@auth0/auth0-react";
import { useAbortController } from '../contexts/AbortControllerContext';
import PermissionGuard from '../contexts/PermissionContext';
import ProjectContext from '../contexts/ProjectContext';
import ControlBar from '../components/structure/ControlBar';
import ControlButton from '../components/structure/ControlButton';
import ItemList from '../components/menuitem/ItemList';
import FullDataGrid from '../components/datagrid/FullDataGrid';
import UploadMetadataDialog from '../components/dialog/UploadMetadataDialog/UploadMetadataDialog';
import SecondaryButton from '../components/buttons/SecondaryButton';
import ControlDivider from '../components/structure/ControlDivider';

import { itemIcons } from '../components/SideItems/SideItems';
import {useFetch} from '../components/auth/useFetch';

// In milliseconds
let minimumLoadingAnimationDuration = 2000;

export default function UploadView(props) {

    const viewname = 'Upload'

    const { getLinkSummary, transferdate, makeTour, setIsLoading } = props 

    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('sm'))
    const isSm = useMediaQuery(theme.breakpoints.down('md'))


    const { isAuthenticated } = useAuth0();
    const { abortGroupRequests, registerController, unregisterController } = useAbortController()
    const projectContext = useContext(ProjectContext);
    const [files, setFiles] = useState([])
    const [fileData, setFileData] = useState({})
    const [activeIndex, setActiveIndex] = useState(null)
    const [openUploadDialog, setOpenUploadDialog] = useState(false)
    const [uploadRef, setUploadRef] = useState(null)
    const [loading, setLoading] = useState(false)
    const [sources, setSources] = useState([])
    const fetchWithAuth = useFetch()


    const fileDialog = (e) => {
        setUploadRef(e.currentTarget)
        setOpenUploadDialog((prevState) => !prevState)
    }
    const clickAway = () => {
        setOpenUploadDialog(false)
    }

    const emptyDataGridCols = [{field: '', headerName: '', flex: 1}

    ]

    useEffect(() => {
        makeTour([
            {
                title: 'Upload Metadata Files',
                target: '#upload-view-upload-button',
                content: 'Upload metadata files using this button. Click this button to bring up the upload dialog.',
                placementBeacon: 'bottom'
            },
            {
                title: 'Uploaded Files',
                target: '#upload-view-uploaded-files',
                content: 'View all the files that have been uploaded for the selected transfer date here. Click on one of the items in this list to preview the contents of the file. This also includes a button to delete the uploaded metadata files.',
                placementBeacon: 'right',
                placement: 'right'
            },
            {
                title: 'Uploaded File Contents',
                target: '#upload-view-uploaded-file',
                content: 'Your selected file will appear here.',
                placement: 'left',
                placementBeacon: 'left',
                locale: {
                    next: 'Finish',
                }
            },
        ])
    }, [])

    useEffect(() => {
        setIsLoading(true)
        const url = `${process.env.REACT_APP_BACKEND_URL}/konsol/sources`
        isAuthenticated && (async () => {
        try {
            await fetchWithAuth(url, {
                headers:{
                    'X-Project-ID': projectContext?.project_db
                }, 
            })
                .then((response) => response.json())
                .then((res) => {
                    setSources(
                        Object.values(res)
                            .filter(item => item.type !== 'sync')
                            .sort((a, b) => (a?.name ?? '')?.localeCompare(b?.name ?? '') )
                    )
                })
        } catch (e) {
            console.error(e);
        }
        })();
    }, [isAuthenticated])

    useEffect(() => {
        setIsLoading(true)
        isAuthenticated && (async () => {
        try {
            const url = `${process.env.REACT_APP_BACKEND_URL}/konsol/uploadlist/?transfer_date=${transferdate}`
            const uploadListController = registerController(`${transferdate}`)
            transferdate && await fetchWithAuth(url, {
                headers: {
                    'X-Project-ID': projectContext?.project_db
                },
                signal: uploadListController.signal
            })
                .then((response) => response.json())
                .then((res) => {
                    // FIXME: This is hardcoded to AvidSync. Ideally this would instead filter by source/metadata type (eg, 'sync') to catch all items of type sync, vs eg metadata or framedata
                    //   But currently we aren't storing the source type in the metadata document, only the source name
                    // For the better implementation that uses the ideal data from the backend, see this file Ln 118
                    res = res.filter(item => item.source !== 'AvidSync')
                    setFiles(res)
                    setTimeout(() => {
                        setIsLoading(false)
                    }, 1000);
                })
                .catch(e => {
                    if(e.name !== 'AbortError'){
                        console.error(e);
                    }
                })
                .finally(() => unregisterController(uploadListController, `${transferdate}`))
        } catch (e) {
            console.error(e);
        }
        })();
    }, [transferdate, isAuthenticated])

    const fetchFile = (index) => {
        setFileData({})
        setLoading(true)
        setIsLoading(true)
        let startTime = new Date();
        setActiveIndex(index)
        const url = `${process.env.REACT_APP_BACKEND_URL}/konsol/uploadfile/?file_name=${files[index]['filename']}&source=${files[index]['source']}`
        abortGroupRequests(`${transferdate}.get.uploadfile`)
        const uploadFileController = registerController(`${transferdate}.get.file`)
        fetchWithAuth(url, {
            headers: {
                'X-Project-ID': projectContext?.project_db
            },
            signal: uploadFileController.signal
        })
            .then((response) => response.json())
            .then((res) => {
                setFileData(res)
                // Handle loading anim time
                let endTime = new Date();
                if (endTime.getTime() - startTime.getTime() < minimumLoadingAnimationDuration){
                let waitTime = minimumLoadingAnimationDuration - (endTime.getTime() - startTime.getTime());
                setTimeout(()=>{
                    setLoading(false)
                    setIsLoading(false)
                }, waitTime)
                } else {
                    setLoading(false)
                    setTimeout(() => {
                        setIsLoading(false)
                    }, 400);
                }
            })
            .catch(e => {
                if(e.name !== 'AbortError'){
                    console.error(e);
                }
            })
            .finally(() => unregisterController(uploadFileController))
    }

    const deleteFile = async (index) => {
        const url = `${process.env.REACT_APP_BACKEND_URL}/konsol/deletefile/?file_name=${files[index]['filename']}&source=${files[index]['source']}&transfer_date=${transferdate}`
        await fetchWithAuth(url, {
            headers: {
                'X-Project-ID': projectContext?.project_db
            },
        })
            .then((response) => response.json())
            .then((res) =>
                setFiles((prev) => {
                    prev.splice(index, 1)
                    return [...prev]
                })
            )
            .then(() => {
                getLinkSummary(transferdate)
            })
            .catch(e => {
                console.error(e);
            })
    }

    return (
        <React.Fragment>
            <Box
                display="flex"
                flexDirection="column"
                height='100%'
                width='100%'
            >
                <ControlBar>
                    <Tabs
                        value={0}
                        indicatorColor="secondary"
                        variant="scrollable"
                        scrollButtons="auto">
                        <Tab
                            label={
                                <Box display="flex" alignItems="center">
                                    {itemIcons[viewname]}
                                    <Box ml={1}>{viewname}</Box>
                                </Box>
                            }
                            style={{ textTransform: 'none', fontWeight: 'bold', color: 'white' }} />
                    </Tabs>

                    
                    {
                        isXs || isSm
                        ?
                        <></>
                        :
                        <React.Fragment>
                            <ControlDivider />
                            <PermissionGuard permission='write:metadata'>
                                <ControlButton
                                    title='Upload File'
                                    clicked={fileDialog}
                                >
                                    <FileUploadIcon id='upload-view-upload-button' style={{fill: 'white'}} />
                                </ControlButton>
                            </PermissionGuard>
                        </React.Fragment>
                    }
  

                </ControlBar>
                
                <UploadMetadataDialog
                    getLinkSummary={() => getLinkSummary(transferdate)}
                    open={openUploadDialog}
                    setOpen={setOpenUploadDialog}
                    sources={sources}
                    transferdate={transferdate}
                    updateParentList={setFiles}
                />
                {/* 
                    Height calculation:
                        100% - 47px(Control Bar Height)
                */}
                    
                <Box
                    display="flex"
                    flexDirection={{xs: 'column', sm: 'row'}}
                    height='calc(100% - 47px)'                    
                    width='100%'
                >
                    <Box
                        id="upload-view-uploaded-files"
                        height={{xs: '25%', sm: '100%'}}
                        width={{xs: '100%', sm: '300px'}}
                        // width="300px"
                        bgcolor='rgba(0,0,0,0.9)'
                        overflow='auto'
                        flexShrink={0}
                    >
                        <PermissionGuard permission='read:metadata'>
                            <List sx={{
                                color: 'white',
                                width: '100%'}}
                            >
                                {files.map((file, index) => {
                                    return (
                                        <ItemList
                                            key={index}
                                            active={index === activeIndex ? true : false}
                                            title={file['filename']} 
                                            label={file['source']} 
                                            onItemClick={() => fetchFile(index)}
                                            iconButton={
                                                <PermissionGuard permission='delete:metadata'>
                                                    <SecondaryButton
                                                        onClick={() => deleteFile(index)}
                                                        hoverColor='red'
                                                        color='rgb(40 40 40)'
                                                        icon={<DeleteForeverIcon />}
                                                    />
                                                </PermissionGuard>
                                            }
                                        />
                                    )
                                })}
                            </List>
                        </PermissionGuard>
                    </Box>

                    <Divider 
                        orientation={{sm: "vertical", xs:"horizontal"}}
                        sx={{ 
                            borderBottomWidth: 5,
                            borderRightWidth: 5,
                            backgroundColor:"rgb(30 80 60)"
                        }}
                    />

                    <Box 
                    height={'100%'} 
                    width ={'100%'}
                    id="upload-view-uploaded-file"
                    overflow="auto"
                    >
                        <Box 
                            height={'100%'}
                            width={'100%'}
                            style={{ display: 'flex', flexDirection: 'column' }}
                        >
                            <FullDataGrid 
                                rows={fileData?.rows} 
                                columns={fileData?.columns} 
                                disableSelection 
                                loading={loading}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </React.Fragment>
    )
}